/**
 * Audit Spend dashboard page (A7Y-1494).
 *
 * Every section is backed by a `@tanstack/react-query` hook reading a
 * BigQuery-fronted admin route, scoped to `env = 'prod'`.
 *
 * The page does not poll, and the API caches for 24h. Measured 2026-08-02 by
 * BQ dry-run, one full refresh of all nine routes scans ~790 MB (~$0.005 at
 * $6.25/TB); on the original 60s interval that was ~$52/month for a tab left
 * open on a second monitor, buying almost nothing — these are daily rollups
 * and trailing-window aggregates in which only the current day can move.
 *
 *   - no `refetchInterval`, so an idle tab costs nothing;
 *   - `refetchOnWindowFocus` with a 24h `staleTime` matching the server's KV
 *     TTL, so returning to the tab is free within the day;
 *   - an explicit Refresh that bypasses BOTH caches — `refetchQueries` for the
 *     client's staleTime and `?force=1` for the server's KV — because a
 *     refresh control in front of a day-long cache would otherwise spin and
 *     return the same payload;
 *   - a "data from …" stamp driven by the server's `generated_at`, NOT by
 *     react-query's `dataUpdatedAt`. With a 24h cache those differ by up to a
 *     day, and showing fetch time would put a fresh timestamp over stale
 *     numbers — the precise failure a long cache invites.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/PageHeader';
import { api } from '@/lib/api';
import { CacheDeltaChart } from './audit-spend/CacheDeltaChart';
import { CostBreachesTable } from './audit-spend/CostBreachesTable';
import { CostBySourceChart } from './audit-spend/CostBySourceChart';
import { DailyTrendChart } from './audit-spend/DailyTrendChart';
import { HeadlineCards } from './audit-spend/HeadlineCards';
import { HotCollections } from './audit-spend/HotCollections';
import { HotEgress } from './audit-spend/HotEgress';
import { OutliersTable } from './audit-spend/OutliersTable';
import { ReconciliationCard } from './audit-spend/ReconciliationCard';
import type {
  ApiEnvelope,
  BySourceData,
  CacheDeltaData,
  CostBreachesData,
  DailyTrendData,
  HeadlineData,
  HotCollectionsData,
  HotEgressData,
  OutliersData,
  ReconcileData,
} from './audit-spend/types';
import { RefreshCw } from 'lucide-react';
import { formatLocal } from './audit-spend/format';
import { AUDIT_SPEND_FETCH_POLICY } from './audit-spend/fetch-policy';

/** Every section fetches under the same policy — see `audit-spend/fetch-policy.ts`. */
function useAuditSpendQuery<T>(key: string[], fn: () => Promise<ApiEnvelope<T>>) {
  return useQuery<ApiEnvelope<T>>({
    queryKey: key,
    queryFn: fn,
    ...AUDIT_SPEND_FETCH_POLICY,
  });
}

export function AuditSpendPage() {
  const [threshold, setThreshold] = useState(0.5);
  const [trendDays, setTrendDays] = useState(30);

  // Set for the duration of an explicit Refresh so the query functions add
  // `?force=1`. A ref rather than state on purpose: flipping state would
  // re-render and re-create every queryFn mid-refetch, and the flag has to be
  // readable by closures react-query already holds.
  const forceRef = useRef(false);

  const headline = useAuditSpendQuery<HeadlineData>(['audit-spend', 'headline'], () =>
    api.getAuditSpendHeadline({ force: forceRef.current })
  );
  const cacheDelta = useAuditSpendQuery<CacheDeltaData>(['audit-spend', 'cache-delta'], () =>
    api.getAuditSpendCacheDelta({ force: forceRef.current })
  );
  const dailyTrend = useAuditSpendQuery<DailyTrendData>(
    ['audit-spend', 'daily-trend', String(trendDays)],
    () => api.getAuditSpendDailyTrend({ days: trendDays, force: forceRef.current })
  );
  // Shares `trendDays` with the chart above it: the days the chart flags red
  // are exactly the days whose audits are listed here.
  const costBreaches = useAuditSpendQuery<CostBreachesData>(
    ['audit-spend', 'cost-breaches', String(trendDays)],
    () => api.getAuditSpendCostBreaches({ days: trendDays, force: forceRef.current })
  );
  const bySource = useAuditSpendQuery<BySourceData>(['audit-spend', 'by-source'], () =>
    api.getAuditSpendBySource({ force: forceRef.current })
  );
  const outliers = useAuditSpendQuery<OutliersData>(
    ['audit-spend', 'outliers', String(threshold)],
    () => api.getAuditSpendOutliers({ threshold, hours: 24, force: forceRef.current })
  );
  const hotCollections = useAuditSpendQuery<HotCollectionsData>(
    ['audit-spend', 'hot-collections'],
    () => api.getAuditSpendHotCollections({ hours: 24, force: forceRef.current })
  );
  const hotEgress = useAuditSpendQuery<HotEgressData>(['audit-spend', 'hot-egress'], () =>
    api.getAuditSpendHotEgress({ hours: 24, force: forceRef.current })
  );
  const reconcile = useAuditSpendQuery<ReconcileData>(['audit-spend', 'reconcile'], () =>
    api.getAuditSpendReconcile({ hours: 24, force: forceRef.current })
  );

  const anyRefreshing =
    headline.isFetching ||
    cacheDelta.isFetching ||
    dailyTrend.isFetching ||
    costBreaches.isFetching ||
    bySource.isFetching ||
    outliers.isFetching ||
    hotCollections.isFetching ||
    hotEgress.isFetching ||
    reconcile.isFetching;

  // One entry per section so the banner and the console log stay in step —
  // deriving both from this list means a new section can't silently go
  // unreported the way the old `a || b || c` chain allowed.
  const sections = [
    { label: 'Headline', endpoint: '/admin/audit-spend/headline', q: headline },
    { label: 'Cache delta', endpoint: '/admin/audit-spend/cache-delta', q: cacheDelta },
    { label: 'Daily trend', endpoint: '/admin/audit-spend/daily-trend', q: dailyTrend },
    { label: 'Cost breaches', endpoint: '/admin/audit-spend/cost-breaches', q: costBreaches },
    { label: 'Cost by source', endpoint: '/admin/audit-spend/by-source', q: bySource },
    { label: 'Outliers', endpoint: '/admin/audit-spend/outliers', q: outliers },
    { label: 'Hot collections', endpoint: '/admin/audit-spend/hot-collections', q: hotCollections },
    { label: 'Hot egress', endpoint: '/admin/audit-spend/hot-egress', q: hotEgress },
    { label: 'Reconcile', endpoint: '/admin/audit-spend/reconcile', q: reconcile },
  ] as const;

  const failed = sections.filter((s) => s.q.isError);
  const anyError = failed.length > 0;

  // Age of the DATA, not of the fetch. The API caches for 24h, so a response
  // that arrived a second ago can be carrying yesterday's scan — using
  // react-query's `dataUpdatedAt` here would render a reassuring "updated just
  // now" over a day-old number, which is the exact failure a long cache
  // invites. Falls back to fetch time only for a payload with no stamp.
  // Hoisted out of the dep array: react-hooks/use-memo rejects a computed
  // expression as a dependency, and the identity of this string is exactly the
  // recompute trigger we want.
  const sectionStampKey = sections
    .map((s) => `${s.q.data?.generated_at ?? ''}:${s.q.dataUpdatedAt}`)
    .join(',');
  const oldestGeneratedAt = useMemo(() => {
    const stamps = sections
      .map((s) => {
        const gen = s.q.data?.generated_at;
        const parsed = gen ? Date.parse(gen) : NaN;
        return Number.isFinite(parsed) ? parsed : s.q.dataUpdatedAt;
      })
      .filter((t): t is number => typeof t === 'number' && t > 0);
    // Oldest, not newest: the stamp has to describe the stalest thing visible.
    return stamps.length ? Math.min(...stamps) : 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionStampKey]);

  const queryClient = useQueryClient();
  const [forcing, setForcing] = useState(false);
  const refreshAll = async () => {
    // Two bypasses are needed, one per cache tier. `refetchQueries` (not
    // `invalidateQueries`) ignores the client's 24h staleTime; `forceRefresh`
    // makes the queryFn send `?force=1` so the server skips KV too. Without
    // both, the button would spin and return the same day-old payload.
    setForcing(true);
    forceRef.current = true;
    try {
      await queryClient.refetchQueries({ queryKey: ['audit-spend'] });
    } finally {
      forceRef.current = false;
      setForcing(false);
    }
  };

  // Log the actual failure. The shared api-client already appends the server's
  // response body to the Error message (`… failed: 500 Internal Server Error -
  // {"message":"…"}`), so the detail exists — this page just never surfaced it,
  // which is what made a real outage take a full manual sweep to diagnose.
  //
  // Log only when the set of failing sections CHANGES; a failure that persists
  // across refreshes would otherwise repeat and bury the first (most useful)
  // occurrence.
  const loggedRef = useRef<string>('');
  useEffect(() => {
    const signature = failed
      .map((s) => `${s.label}:${(s.q.error as Error | null)?.message ?? 'unknown'}`)
      .join('|');
    if (signature === loggedRef.current) return;
    loggedRef.current = signature;
    if (!failed.length) return;

    console.error(
      `[audit-spend] ${failed.length} of ${sections.length} section(s) failed to load:`
    );
    for (const s of failed) {
      const err = s.q.error as Error | null;
      console.error(
        `[audit-spend]   ✗ ${s.label}  (GET ${s.endpoint})\n` +
          `      ${err?.message ?? 'no error message available'}`,
        err
      );
    }
  }, [failed, sections.length]);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Audit Spend"
        subtitle="Per-audit infrastructure and vendor cost telemetry, read from BigQuery on demand."
      >
        <div className="flex items-center gap-3 text-xs text-zinc-500">
          <span title="When the underlying BigQuery scan ran for the oldest section on screen — not when the page last fetched. Results are cached for 24h; Refresh bypasses the cache and re-scans (~790 MB).">
            {anyRefreshing
              ? 'updating…'
              : oldestGeneratedAt
                ? `data from ${formatLocal(new Date(oldestGeneratedAt).toISOString())}`
                : 'not loaded yet'}
          </span>
          <button
            type="button"
            onClick={() => void refreshAll()}
            disabled={anyRefreshing || forcing}
            className="inline-flex items-center gap-1 rounded border border-zinc-300 dark:border-zinc-600 px-2 py-1 hover:bg-zinc-50 dark:hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className={`h-3 w-3 ${anyRefreshing || forcing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </PageHeader>

      {anyError && (
        <div className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/40 p-3 text-sm text-red-700 dark:text-red-300">
          <span className="font-medium">
            {failed.length === 1
              ? `${failed[0].label} failed to load.`
              : `${failed.length} sections failed to load: ${failed
                  .map((s) => s.label)
                  .join(', ')}.`}
          </span>{' '}
          Those cards show their last-good values. Full detail is in the browser console — and in
          the local `wrangler dev` terminal when running against a local API.
          <ul className="mt-2 space-y-1 font-mono text-xs opacity-90">
            {failed.map((s) => (
              <li key={s.label}>
                {s.endpoint} — {(s.q.error as Error | null)?.message ?? 'unknown error'}
              </li>
            ))}
          </ul>
        </div>
      )}

      <HeadlineCards envelope={headline.data} loading={headline.isLoading} />

      <CacheDeltaChart envelope={cacheDelta.data} loading={cacheDelta.isLoading} />

      <DailyTrendChart
        envelope={dailyTrend.data}
        loading={dailyTrend.isLoading}
        days={trendDays}
        onDaysChange={setTrendDays}
      />

      <CostBreachesTable
        envelope={costBreaches.data}
        loading={costBreaches.isLoading}
        days={trendDays}
      />

      <CostBySourceChart envelope={bySource.data} loading={bySource.isLoading} />

      <OutliersTable
        envelope={outliers.data}
        loading={outliers.isLoading}
        threshold={threshold}
        onThresholdChange={setThreshold}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <HotCollections envelope={hotCollections.data} loading={hotCollections.isLoading} />
        <HotEgress envelope={hotEgress.data} loading={hotEgress.isLoading} />
      </div>

      <ReconciliationCard envelope={reconcile.data} loading={reconcile.isLoading} />
    </div>
  );
}
